import React, { useState, lazy, Suspense } from 'react';
import useGameStore from '../../store/gameStore';
import { Sparkles, Trash2, Save, DoorOpen, CreditCard, ArrowUp, Flame, Snowflake, Box, Grid3X3 } from 'lucide-react';
import { toast } from '../ui/sonner';
import { formatMoney } from '../../utils/format';
import {
  PARCOURS, TYPES_ZONE, computeParcours, suggererParcours, famillesActives, coutParcours,
  typeCelluleFixe, cellulesVides, getAffinites, merchSecteur, typeMagasinSecteur,
} from '../../utils/merchandising';
import { PaletteMerch, couleurItem, libelleItem } from './PaletteMerch';
import { ScoreMerch } from './ScoreMerch';

const Magasin3D = lazy(() => import('./Magasin3D'));

const styleChaleur = (h) => ({ background: `linear-gradient(180deg, rgba(249,115,22,${(h * 0.55).toFixed(2)}) 0%, rgba(59,130,246,${((1 - h) * 0.45).toFixed(2)}) 100%)` });
const libelleChaleur = (h) => (h >= 0.85 ? 'zone chaude' : h >= 0.6 ? 'zone tiède' : 'zone froide');

const valeurCellule = (cell) => (!cell ? null : cell.type === 'famille' ? cell.famId : cell.type);
const celluleDepuisValeur = (v) => (!v || v === '__vide' ? null : TYPES_ZONE.some(t => t.id === v) ? { type: v } : { type: 'famille', famId: v });

/** Plan de magasin interactif : grille 3 × 4, entrée et caisses fixes, zones chaudes/froides. */
export const ParcoursEditor = ({ secteurId: secteurProp = null }) => {
  const retail = useGameStore(s => s.retail);
  const treasury = useGameStore(s => s.treasury) || 0;
  const month = useGameStore(s => s.month) || 1;
  const appliquerParcours = useGameStore(s => s.appliquerParcours);
  const secteurId = secteurProp || retail.secteur;
  const fams = famillesActives(retail, secteurId);
  const cfg = merchSecteur(retail, secteurId);
  const opts = { secteurId, month };
  const [draft, setDraft] = useState(() => cfg.parcours?.cells || cellulesVides());
  const [brush, setBrush] = useState(null);
  const [vue3d, setVue3d] = useState(true);

  const fx = computeParcours(retail, draft, opts);
  const dejaConfigure = !!cfg.parcours;
  const cout = dejaConfigure ? coutParcours(typeMagasinSecteur(retail, secteurId)) : 0;
  const modifie = JSON.stringify(draft) !== JSON.stringify(cfg.parcours?.cells || cellulesVides());
  const best = fams.find(f => f.id === fx.bestId) || fams.reduce((a, f) => (f.part_demande > (a?.part_demande || 0) ? f : a), null);

  const poser = (i, value) => setDraft(d => d.map((c, j) => (j === i ? celluleDepuisValeur(value) : c)));
  const onDrop = (i, e) => {
    e.preventDefault();
    const data = e.dataTransfer.getData('text/plain');
    if (data.startsWith('move:')) {
      const from = Number(data.slice(5));
      if (from === i) return;
      setDraft(d => d.map((c, j) => (j === i ? d[from] : j === from ? d[i] : c)));
    } else if (data) poser(i, data);
  };

  const appliquer = () => {
    const res = appliquerParcours(draft, secteurId);
    if (res?.ok) toast('Parcours client appliqué', { description: `Score ${res.score}/100 · ${res.cout ? `${formatMoney(res.cout)} de réagencement` : 'mise en place offerte'}. Effets dès demain.` });
    else if (res?.reason === 'fonds') toast.error('Trésorerie insuffisante pour le réagencement.');
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-4" data-testid="parcours-editor">
      <div className="space-y-4">
        <PaletteMerch fams={fams} zones={TYPES_ZONE} selection={brush} onSelect={setBrush} testPrefix="parcours" />

        <div className="flex items-center justify-between">
          <p className="text-[10px] uppercase tracking-widest text-slate-500">{vue3d ? 'Magasin 3D — flux clients animé' : 'Plan du magasin'}</p>
          <button type="button" onClick={() => setVue3d(v => !v)} className="px-2.5 py-1 rounded text-[11px] font-bold border border-ink-600 text-slate-300 hover:bg-ink-800 flex items-center gap-1.5" data-testid="parcours-toggle-3d">
            {vue3d ? <><Grid3X3 className="w-3.5 h-3.5" /> Masquer la 3D</> : <><Box className="w-3.5 h-3.5" /> Afficher le magasin 3D</>}
          </button>
        </div>
        {vue3d && (
          <Suspense fallback={<div className="h-[380px] rounded-lg border border-ink-600 bg-ink-950 flex items-center justify-center text-xs text-slate-500" data-testid="magasin-3d-loading">Chargement du magasin 3D…</div>}>
            <Magasin3D cells={draft} chaleur={fx.chaleur} fams={fams} secteurId={secteurId} bestId={fx.bestId} libelle={(v) => libelleItem(v, fams)} onCellClick={(i) => { if (brush) poser(i, brush); }} onCellClear={(i) => poser(i, null)} />
          </Suspense>
        )}

        <div className="rounded-lg border border-ink-600 bg-ink-900/60 p-3" data-testid="parcours-grille">
          <div className="flex items-center justify-between text-[10px] text-slate-500 mb-2">
            <span className="flex items-center gap-1"><Snowflake className="w-3 h-3 text-sky-400" /> Fond du magasin — zones froides</span>
            <span className="flex items-center gap-1">Produit d'appel : <b className="text-slate-200">{best?.nom}</b></span>
          </div>
          <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${PARCOURS.colonnes}, minmax(0, 1fr))` }}>
            {draft.map((cell, i) => {
              const fixe = typeCelluleFixe(i);
              const v = valeurCellule(cell);
              const h = fx.chaleur[i];
              if (fixe) {
                return (
                  <div key={i} className="h-24 rounded-md border-2 border-ink-500 bg-ink-800 flex flex-col items-center justify-center gap-1 text-slate-200" data-testid={`parcours-cell-${i}`} data-value={fixe}>
                    {fixe === 'entree' ? <DoorOpen className="w-5 h-5 text-accent" /> : <CreditCard className="w-5 h-5 text-accent" />}
                    <span className="text-[10px] font-bold uppercase tracking-wider">{fixe === 'entree' ? 'Entrée' : 'Caisses'}</span>
                    {fixe === 'entree' && <ArrowUp className="w-3 h-3 text-slate-500 animate-bounce" />}
                  </div>
                );
              }
              return (
                <div
                  key={i}
                  role="button"
                  tabIndex={0}
                  draggable={!!v}
                  onDragStart={e => { e.dataTransfer.setData('text/plain', `move:${i}`); }}
                  onDragOver={e => e.preventDefault()}
                  onDrop={e => onDrop(i, e)}
                  onClick={() => { if (brush) poser(i, brush); }}
                  onKeyDown={e => { if (e.key === 'Enter' && brush) poser(i, brush); }}
                  onContextMenu={e => { e.preventDefault(); poser(i, null); }}
                  title={`${libelleItem(v, fams)} — ${libelleChaleur(h)} (${Math.round(h * 100)} % de passage)`}
                  style={styleChaleur(h)}
                  className={`relative h-24 rounded-md border flex flex-col items-center justify-center gap-1 px-1 text-center select-none transition-transform hover:scale-[1.02] cursor-pointer ${v ? 'border-ink-500' : 'border-dashed border-ink-600'} ${brush ? 'cursor-crosshair' : ''}`}
                  data-testid={`parcours-cell-${i}`}
                  data-value={v || ''}
                >
                  <span className="absolute top-1 right-1 text-[9px] font-mono text-slate-300/80 flex items-center gap-0.5">{h >= 0.85 ? <Flame className="w-3 h-3 text-orange-400" /> : h < 0.6 ? <Snowflake className="w-3 h-3 text-sky-300" /> : null}{Math.round(h * 100)} %</span>
                  {v ? (
                    <span className={`px-2 py-1 rounded text-[10px] font-bold text-white leading-tight ${couleurItem(v, fams)}`}>{libelleItem(v, fams)}</span>
                  ) : (
                    <span className="text-[10px] text-slate-400">{libelleChaleur(h)}<br />vide</span>
                  )}
                  {v && fx.bestId && v === fx.bestId && <span className="text-[8px] uppercase tracking-wider text-amber-300">produit d'appel</span>}
                </div>
              );
            })}
          </div>
          <div className="flex items-center justify-between text-[10px] text-slate-500 mt-2">
            <span className="flex items-center gap-1"><Flame className="w-3 h-3 text-orange-400" /> Façade — zones chaudes (sens de circulation : entrée → fond → caisses)</span>
            <span>Clic droit : vider · glisser pour échanger</span>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => setDraft(suggererParcours(retail, opts))} className="px-3 py-2 rounded text-xs font-bold border border-accent/60 text-accent hover:bg-accent/10 flex items-center gap-1.5" data-testid="parcours-suggestion">
            <Sparkles className="w-3.5 h-3.5" /> Suggestion automatique
          </button>
          <button type="button" onClick={() => setDraft(cellulesVides())} className="px-3 py-2 rounded text-xs font-bold border border-ink-600 text-slate-300 hover:bg-ink-800 flex items-center gap-1.5" data-testid="parcours-vider">
            <Trash2 className="w-3.5 h-3.5" /> Tout vider
          </button>
          <button
            type="button"
            onClick={appliquer}
            disabled={!modifie || treasury < cout}
            className="ml-auto px-4 py-2 rounded text-xs font-bold bg-accent text-white hover:bg-accent-hover disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
            data-testid="parcours-appliquer"
          >
            <Save className="w-3.5 h-3.5" /> Appliquer {cout ? `(${formatMoney(cout)} de réagencement)` : '(mise en place offerte)'}
          </button>
        </div>

        <div className="surface-card p-3" data-testid="parcours-affinites">
          <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">Familles complémentaires (cross-merchandising)</p>
          <div className="flex flex-wrap gap-2">
            {getAffinites(secteurId).map(([a, b]) => (
              <span key={`${a}-${b}`} className="text-[11px] text-slate-300 rounded border border-ink-700 px-2 py-1 flex items-center gap-1.5">
                <span className={`w-2 h-2 rounded-sm ${couleurItem(a, fams)}`} />{libelleItem(a, fams)} <span className="text-slate-500">+</span> <span className={`w-2 h-2 rounded-sm ${couleurItem(b, fams)}`} />{libelleItem(b, fams)}
              </span>
            ))}
          </div>
        </div>
      </div>

      <ScoreMerch
        titre="Score parcours client"
        fx={fx}
        testPrefix="parcours"
        effets={[
          { key: 'trafic', label: 'Trafic', value: fx.traficPct },
          { key: 'conversion', label: 'Conversion', value: fx.conversionPct },
          { key: 'panier', label: 'Panier moyen', value: fx.panierPct },
        ]}
      />
    </div>
  );
};
